import { getGroupAuthType } from "@/utils/authToken";
import { httpAuth } from "@/api/httpAuth";
import { httpAuthBackground } from "@/api/httpBackground";
import { httpPublic } from "@/api/httpPublic";

import { GROUP_AUTH_HEADER_NAME } from "../constants";

const commonPatientRequest = ({ context, lastPath, searchParams, urlParams, version }) => {
  return httpAuth
    .post(`/${version}/patient_management/${urlParams.patientId}/${lastPath}`, {
      context,
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      ...(searchParams && { searchParams }),
    })
    .json();
};

export const sharedRequests = {
  account_lookup: function ({ context, data } = {}) {
    return httpPublic.post("/v1/referral/account_lookup", { context, json: data }).json();
  },

  add_provider_to_care_team: function ({ context, searchParams, urlParams }) {
    const { providerId } = searchParams;
    const { patientId } = urlParams;

    return httpAuth
      .post(`/v1/patient_management/${patientId}/add_provider_to_care_team`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { providerId },
      })
      .json();
  },

  available_schedules: function ({ context, urlParams }) {
    // ky replaces url.search entirely when searchParams is used, so timezone must live in
    // the URL string to avoid being clobbered; encodeURIComponent preserves 1:1 behaviour
    let url = `/v3/patient/available_schedule?date=${urlParams.dateString}&timezone=${encodeURIComponent(urlParams.tz)}`;

    if (urlParams.providerId) url += `&provider_id=${urlParams.providerId}`;
    if (urlParams.patientId) url += `&patient_id=${urlParams.patientId}`;
    if (urlParams.providerType) url += `&provider_type=${urlParams.providerType}`;
    if (urlParams.allottedTime) url += `&allotted_time=${urlParams.allottedTime}`;
    if (urlParams.eventType) url += `&event_type=${urlParams.eventType}`;
    if (urlParams.call_id) url += `&call_id=${urlParams.call_id}`;

    return httpAuth
      .post(url, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  create_event_notification: function ({ context, data } = {}) {
    return httpAuth.post("v1/event_notifications/create", { context, json: data }).json();
  },

  demographic_eligibility: function ({ context, data } = {}) {
    return httpPublic.post("/v1/referral/demographic_eligibility", { context, json: data }).json();
  },

  eligible_states: function ({ context } = {}) {
    return httpPublic.post("/v1/referral/eligible_states", { context }).json();
  },

  fetch_a5_screening_details: function ({ context, headers, searchParams }) {
    if (window.localStorage.getItem("token")) {
      return httpAuth
        .post("/v1/a5_screening/get", {
          context,
          headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
          searchParams,
        })
        .json();
    }

    return httpPublic
      .post("/v1/a5_screening/get", {
        context,
        headers: { Authorization: "DOB " + headers["dob"] },
        searchParams,
      })
      .json();
  },

  fetch_a5_screening_list: function ({ context, searchParams }) {
    return httpAuth
      .post("/v1/a5_screening/list", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(searchParams.practice_id && {
          searchParams: { practice_id: searchParams.practice_id },
        }),
      })
      .json();
  },

  fetch_billable_activities: function ({ context, searchParams }) {
    return httpAuth
      .post("/v1/billing/get_activities", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: {
          endDate: searchParams.endDate,
          isProcessed: searchParams.isProcessed,
          startDate: searchParams.startDate,
          ...(searchParams?.providerId && { providerId: searchParams.providerId }),
          ...(searchParams?.practiceId && { practiceId: searchParams.practiceId }),
        },
      })
      .json();
  },

  fetch_patient_activity_feed: function ({ context, urlParams }) {
    return commonPatientRequest({ context, lastPath: "activity", urlParams, version: "v1" });
  },

  fetch_patient_avatar: function ({ context, data, urlParams }) {
    return this.fetch_patient_file({ context, data, urlParams });
  },

  fetch_patient_care_plan: function ({ context, urlParams }) {
    return commonPatientRequest({ context, lastPath: "care_plan", urlParams, version: "v1" });
  },

  fetch_patient_care_plan_v2: function ({ context, urlParams }) {
    return httpAuth
      .post(`/v1/care-plan/${urlParams.patientId}/get_care_plan`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  fetch_patient_careteam: function ({ context, searchParams = {}, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/care_team`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json();
  },

  fetch_patient_clinical_assessment: function ({ context, searchParams, urlParams }) {
    return commonPatientRequest({
      context,
      lastPath: "get_clinical_assessment",
      searchParams: { type: searchParams.type },
      urlParams,
      version: "v1",
    });
  },

  fetch_patient_details: function ({ context, urlParams }) {
    return commonPatientRequest({ context, lastPath: "patient_details", urlParams, version: "v1" });
  },

  fetch_patient_file: function ({ context, data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/get_file`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  fetch_patient_notes_summary: function ({ context, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/get_notes_summary`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  fetch_patient_phq9_gad7: function ({ context, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/screening-data`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { day: urlParams.day, type: urlParams.type },
      })
      .json();
  },

  // TODO: migrate to sharedRequestsTyped.fetch_patient_progress_notes — remaining callers:
  //   ProviderVideoWorkflowStep.js, ProgressNotes.js, DischargeData.js,
  //   ViewNote.js, NewNoteV2.js, DraftNoteView.js, DraftNoteViewV2.js, PrintNoteView.js
  fetch_patient_progress_notes: function ({ context, searchParams = {}, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/get_progress_notes`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json();
  },

  fetch_patient_report: function ({ context, urlParams }) {
    return commonPatientRequest({ context, lastPath: "report", urlParams, version: "v1" });
  },

  fetch_patients_reports: function ({ context, data, searchParams } = {}) {
    return httpAuth
      .post("/v2/provider/patient-reports", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        searchParams: { practice_id: searchParams["practice_id"] },
      })
      .json();
  },

  fetch_practice: function ({ context, searchParams }) {
    return httpAuth
      .post(`/v1/practice/practice`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { practice_id: searchParams.practiceId },
      })
      .json();
  },

  fetch_practice_provider: function ({ context, searchParams }) {
    return httpAuth
      .post(`/v1/practice/practice_provider`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: {
          practice_id: searchParams.practiceId,
          provider_id: searchParams.providerId,
        },
      })
      .json();
  },

  fetch_providers: function ({ context, searchParams } = {}) {
    const { insurance, provider_type } = searchParams;

    return httpAuth
      .post("/v1/provider/get_providers", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: {
          ...(insurance && { insurance }),
          ...(provider_type && { provider_type }),
        },
      })
      .json();
  },

  fetch_referral: function ({ context, searchParams }) {
    return httpAuth
      .post("/v1/provider/get_referral", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { referralCode: searchParams.referralCode },
      })
      .json();
  },

  fetch_report: function ({ context, searchParams, urlParams }) {
    return httpAuth
      .get(`/v1/reports/data/${urlParams.patientId}`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { type: searchParams.type },
      })
      .json();
  },

  fetch_twilio_token: function ({ context, data }) {
    return httpAuth
      .post("/v3/video_call/twilio_token", {
        context: { conditionalExceptionReporting: true, ...context },
        json: data,
      })
      .json();
  },

  generate_otc: function ({ context, searchParams }) {
    return httpAuth
      .post("v1/auth/generate_otc", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { patient_id: searchParams.patientId },
      })
      .json();
  },

  get_billing_reports: function ({ context, data, searchParams }) {
    return httpAuth
      .post("/v1/billing/get_reports", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(searchParams?.practiceId && {
          searchParams: { practiceId: searchParams.practiceId },
        }),
      })
      .json();
  },

  get_earliest_date_allowed_to_schedule: function ({ context, searchParams }) {
    return httpAuth
      .post("/v1/patient/earliest_date_allowed_to_schedule", {
        context,
        searchParams: {
          timezone: searchParams.tz,
          ...(searchParams.providerType && { provider_type: searchParams.providerType }),
        },
      })
      .json();
  },

  get_files: async function ({ context, data }) {
    return httpAuth
      .post("/v1/provider/get_files", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  // TODO: migrate to sharedRequestsTyped.fetch_note_addendums — remaining callers:
  //   ProviderVideoWorkflowStep.js, PrintNoteView.js
  get_note_addendums: function ({ context, data, urlParams }) {
    return httpAuth
      .post(`v1/patient_management/${urlParams.patientId}/get_note_addendums`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  get_patient_call_question_sets: function ({ context, data }) {
    return httpAuth
      .post("/v2/video_call/get_patient_question_sets", { context, json: data })
      .json();
  },

  get_patient_diagnosis: async function ({ context, urlParams } = {}) {
    return httpAuth
      .post(`v1/patient_management/${urlParams.patientId}/get_patient_diagnosis`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  get_post_session_survey_question_set: function ({ callId, context }) {
    return httpAuth
      .post("/v1/video_call/get_post_session_survey_question_set", {
        context,
        json: {},
        searchParams: { call_id: callId },
      })
      .json();
  },

  get_presigned_url: function ({ context, data, version = "v4" }) {
    return httpAuth
      .post(`/${version}/provider/get_presigned_url`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  get_progress_notes_by_pcp_practice_id: function ({ context, data, searchParams }) {
    return httpAuth
      .post("v1/patient_management/x/get_progress_notes_by_pcp_practice_id", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(searchParams?.pcpPracticeId && {
          searchParams: { pcp_practice_id: searchParams.pcpPracticeId },
        }),
      })
      .json();
  },

  get_progress_notes_by_practice_id: function ({ context, data, searchParams }) {
    return httpAuth
      .post("v1/patient_management/x/get_progress_notes_by_practice_id", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(searchParams?.practiceId && { searchParams: { practice_id: searchParams.practiceId } }),
      })
      .json();
  },

  get_progress_notes_by_provider_id: function ({ context, data, searchParams }) {
    return httpAuth
      .post("v1/patient_management/x/get_progress_notes_by_provider_id", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(searchParams?.providerId && { searchParams: { provider_id: searchParams.providerId } }),
      })
      .json();
  },

  poll_billing_reports: function ({ context, data, searchParams }) {
    return httpAuth
      .post("/v1/billing/get_report_meta_data", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        searchParams,
      })
      .json();
  },

  post_exception_report: function ({ context, report }) {
    return httpAuth.post("/v1/user_support/exception_report", { context, json: report }).json();
  },

  post_ratings: function ({ context, data }) {
    return httpAuth.post("/v1/video_call/post_ratings", { context, json: data }).json();
  },

  post_ratings_v2: function ({ context, data }) {
    return httpAuth
      .post("/v2/video_call/post_ratings", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  process_billing_activity: function ({ context, data }) {
    return httpAuth
      .post("/v1/billing/create_report", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  remove_appointment: function ({ context, data, searchParams }) {
    return httpAuth
      .post("/v2/video_call/delete_appointment", {
        context,
        json: data,
        searchParams: { call_id: searchParams.callId },
      })
      .json();
  },

  report_video_call_error: function ({ context, data }) {
    return httpAuthBackground.post("/v1/video_call/error_logging", { context, json: data }).json();
  },

  send_on_demand_welcome_email: async function ({ context, urlParams } = {}) {
    return httpAuth
      .post(`v1/patient_management/${urlParams.patientId}/send_on_demand_welcome_email`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  set_password: function ({ context, data } = {}) {
    return httpAuth.post("/v1/auth/account_set_up", { context, json: data }).json();
  },

  start_file_generation: function ({ context, data, headers } = {}) {
    return httpAuth
      .post("v1/file_generation/start_execution", { context, headers, json: data })
      .json();
  },

  update_a5_screening_details: function ({ context, data, headers }) {
    if (window.localStorage.getItem("token")) {
      return httpAuth
        .post("/v1/a5_screening/update", {
          context,
          headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
          json: data,
        })
        .json();
    }

    return httpPublic
      .post("/v1/a5_screening/update", {
        context,
        headers: { Authorization: "DOB " + headers["dob"] },
        json: data,
      })
      .json();
  },

  update_call: function ({ context, data }) {
    return httpAuth.post("/v1/video_call/update_call", { context, json: data }).json();
  },

  update_insurance: function ({ context, data, headers } = {}) {
    return httpAuth.post("/v1/referral/update_insurance", { context, headers, json: data }).json();
  },

  update_provider_report: function ({ context, data, searchParams }) {
    return httpAuth
      .post("/v1/provider/update_provider_report", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        searchParams: {
          ...(searchParams?.practiceId && { practiceId: searchParams.practiceId }),
          ...(searchParams?.providerId && { providerId: searchParams.providerId }),
        },
      })
      .json();
  },

  update_referral: function ({ context, data }) {
    return httpAuth
      .post("/v1/provider/update_referral", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  write_file: async function ({ context, data }) {
    return httpAuth
      .post("/v1/provider/write_file", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },
};
